import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import moment, { Moment } from 'moment';

import workspaceService from '@app/libs/workspaceService';
import {
  CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_COMPLETE,
  CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_FAILURE,
  CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_REQUEST,
  CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_SUCCESS,
  CONTRACT_AUTHENTICATION_EXCEED_COUNT_INITIALIZE,
  CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_FAILURE,
  CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_REQUEST,
  CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_SUCCESS,
  CONTRACT_AUTHENTICATION_INITIALIZE,
  CONTRACT_AUTHENTICATION_MESSAGE_READ,
  CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_FAILURE,
  CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_REQUEST,
  CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_SUCCESS,
  CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_FAILURE,
  CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_REQUEST,
  CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_SUCCESS,
  CONTRACT_AUTHENTICATION_PHONE_COMPLETE_FAILURE,
  CONTRACT_AUTHENTICATION_PHONE_COMPLETE_SUCCESS,
  CONTRACT_AUTHENTICATION_PHONE_DETAIL_STATUS_INITIALIZE,
  CONTRACT_AUTHENTICATION_PHONE_DETAIL_STATUS_UPDATE,
  CONTRACT_AUTHENTICATION_PHONE_RETRY_FAILURE,
  CONTRACT_AUTHENTICATION_PHONE_RETRY_REQUEST,
  CONTRACT_AUTHENTICATION_PHONE_RETRY_SUCCESS,
  CONTRACT_AUTHENTICATION_UPDATE_FAILURE_COUNT,
} from '@app/store/actionTypes';
import ContractAuthentication from '@app/store/contract-authentication/ContractAuthentication';
import ContractAuthenticationStatus from '@app/store/contract-authentication/ContractAuthenticationStatus';
import StatusAsAuthKey from '@app/store/contract-authentication/StatusAsAuthKey';
import { getDocumentsParticipantsAuths } from '@app/store/transport-layer/document/documents/participants/auths';
import { getDocumentsParticipantsAuthsKey } from '@app/store/transport-layer/document/documents/participants/auths/key';
import { getDocumentsParticipantsMessageFromRequester } from '@app/store/transport-layer/document/documents/participants/message-from-requester';

import ContractAuthenticationDetailStatus from './ContractAuthenticationDetailStatus';

/**
 * 계약 생성자가 서명자에게 보낸 메세지의 수신 완료 여부를 변경한다.
 */
export function contractAuthenticationMessageRead() {
  return {
    type: CONTRACT_AUTHENTICATION_MESSAGE_READ,
  };
}

export function contractAuthenticationDigitalCertificateComplete() {
  return {
    type: CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_COMPLETE,
  };
}

export function contractAuthenticationDigitalCertificateGuideCheck(payload: {
  authenticationId: string;
  answer: string;
}) {
  return {
    type: CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_REQUEST,
    payload,
  };
}

export function contractAuthenticationDigitalCertificateGuideCheckSuccess(payload: {
  answer: string;
}) {
  return {
    type: CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_SUCCESS,
    payload,
  };
}

export function contractAuthenticationDigitalCertificateGuideCheckFailure(payload: {
  error?: AxiosError;
  failureInfo?: {
    failureCount: number;
    lastFailedAt?: string;
    retryAt: Moment | null;
  };
}) {
  return {
    type: CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_FAILURE,
    payload,
    error: true,
  };
}

/**
 * 접근암호인증을 시도한다.
 * @param id - 접근암호인증 아이디
 * @param answer - 접근암호에 대한 답
 */
export function contractAuthenticationPasswordAuthenticateRequest(id: string, answer: string) {
  return {
    type: CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_REQUEST,
    payload: {
      answer,
      id,
    },
  };
}

export function contractAuthenticationPasswordAuthenticateSuccess(payload: {
  failureCount: number;
  lastFailedAt?: string;
  retryAt: Moment | null;
  status: ContractAuthenticationStatus;
}) {
  return {
    type: CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_SUCCESS,
    payload,
  };
}

export function contractAuthenticationPasswordAuthenticateFailure(payload: {
  error?: AxiosError;
  failureInfo?: {
    failureCount: number;
    lastFailedAt?: string;
    retryAt: Moment | null;
    status: ContractAuthenticationStatus;
  };
}) {
  return {
    type: CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_FAILURE,
    payload,
    error: true,
  };
}

/**
 * 휴대폰 본인인증 완료 여부를 확인한다.
 */

export function contractAuthenticationPhoneCompleteSuccess(payload: {
  authenticationDetailStatus: ContractAuthenticationDetailStatus;
  failureCount: number;
  lastFailedAt?: string;
  retryAt: Moment | null;
  status: ContractAuthenticationStatus;
  statusAsAuthKey?: StatusAsAuthKey;
}) {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_COMPLETE_SUCCESS,
    payload,
  };
}

export function contractAuthenticationPhoneCompleteFailure(payload: {
  authenticationDetailStatus: ContractAuthenticationDetailStatus;
  error?: AxiosError;
  errorMessage?: string;
  failureInfo?: {
    failureCount: number;
    lastFailedAt?: string;
    leftFailureCount: number;
    retryAt: Moment | null;
  };
}) {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_COMPLETE_FAILURE,
    payload,
    error: true,
  };
}

/**
 * 휴대폰 본인인증을 재시도한다.
 */
export function contractAuthenticationPhoneRetryRequest(id: string) {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_RETRY_REQUEST,
    payload: { id },
  };
}

export function contractAuthenticationPhoneRetrySuccess(payload: { answer: string }) {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_RETRY_SUCCESS,
    payload,
  };
}

export function contractAuthenticationPhoneRetryFailure(payload: {
  authenticationDetailStatus: ContractAuthenticationDetailStatus;
  error?: AxiosError;
  errorMessage?: string;
  failureInfo?: {
    failureCount: number;
    lastFailedAt?: string;
    leftFailureCount: number;
    retryAt: Moment | null;
  };
}) {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_RETRY_FAILURE,
    payload,
    error: true,
  };
}

/**
 * 휴대폰 본인인증시 뒷자리 번호 인증을 진행한다.
 */
export function contractAuthenticationPhoneCertificateRequest(id: string, answer: string) {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_REQUEST,
    payload: { answer, id },
  };
}

export function contractAuthenticationPhoneCertificateSuccess(payload: { answer: string }) {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_SUCCESS,
    payload,
  };
}

export function contractAuthenticationPhoneCertificateFailure(payload: {
  authenticationDetailStatus: ContractAuthenticationDetailStatus;
  error?: AxiosError;
  errorMessage?: string;
  failureInfo?: {
    failureCount: number;
    lastFailedAt?: string;
    leftFailureCount: number;
    retryAt: Moment | null;
  };
}) {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_FAILURE,
    payload,
    error: true,
  };
}

export function fetchAuthenticationFailureStatus(payload: {
  authenticationId: string;
  authenticationKey: string;
  documentId: string;
  participantId: string;
}) {
  return {
    type: CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_REQUEST,
    payload,
  };
}

export function fetchAuthenticationFailureStatusSuccess(payload: {
  authType: 'MOBILE_IDENTIFICATION' | 'D_CERT';
  failureCount: number;
  lastFailedAt?: string;
  retryAt: Moment | null;
}) {
  return {
    type: CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_SUCCESS,
    payload,
  };
}

export function fetchAuthenticationFailureStatusFailure() {
  return {
    type: CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_FAILURE,
    error: true,
  };
}

export function contractAuthenticationPhoneDetailStatusInitialize() {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_DETAIL_STATUS_INITIALIZE,
  };
}

export function contractAuthenticationPhoneDetailStatusUpdate(payload: {
  authenticationDetailStatus: ContractAuthenticationDetailStatus;
}) {
  return {
    type: CONTRACT_AUTHENTICATION_PHONE_DETAIL_STATUS_UPDATE,
    payload,
  };
}
/**
 * 요청자가 해당 서명자에게 설정한 메시지를 불러온다.
 */
export const fetchMessageFromRequester = createAsyncThunk(
  'contract-authentication/fetchMessageFromRequester',
  getDocumentsParticipantsMessageFromRequester
);

/**
 * 계약 진행시 필요한 데이터를 불러온다.
 */
export const fetchContractAuthentication = createAsyncThunk<
  {
    authentication: ContractAuthentication;
    authKey: string | null;
  },
  { documentId: string; participantId: string }
>('contract-authentication/fetchContractAuthentication', async ({ documentId, participantId }) => {
  const workspaceId = workspaceService.getWorkspaceId();
  const authsData = await getDocumentsParticipantsAuths({
    documentId,
    participantId,
    workspaceId,
  });

  if (!authsData) {
    return {
      authentication: {
        digitalCertificate: null,
        mobile: null,
        password: null,
      },
      authKey: null,
    };
  }

  const authKey = await getDocumentsParticipantsAuthsKey({
    documentId,
    participantId,
    workspaceId,
  });

  const transformedAuthentication: ContractAuthentication = {
    digitalCertificate: null,
    mobile: null,
    password: null,
  };

  authsData.authentications.forEach((auth) => {
    const splittedGuide: string[] = auth.authGuide?.split('|') ?? [];
    let retryAt = null;
    if (auth.failureCount !== 0 && auth.failureCount % 5 === 0 && auth.lastFailedAt) {
      retryAt = moment(auth.lastFailedAt).add(10, 'm');
    }

    if (auth.authType === 'D_CERT') {
      transformedAuthentication.digitalCertificate = {
        authType: auth.authType,
        authenticationId: auth.authenticationId,
        bizNumber: splittedGuide[1],
        bizNumberLastDigits: null,
        companyName: splittedGuide[0],
        failureCount: auth.failureCount,
        lastFailedAt: auth.lastFailedAt,
        retryAt,
        // FIXME: (standardize-document-content): 사용되지 않습니다. 추후 타입 정리가 필요합니다.
        status: auth.status,
      };
    }

    if (auth.authType === 'MOBILE_IDENTIFICATION') {
      transformedAuthentication.mobile = {
        authType: auth.authType,
        authenticationId: auth.authenticationId,
        failureCount: auth.failureCount,
        lastFailedAt: auth.lastFailedAt,
        phoneNumber: splittedGuide[1],
        phoneNumberLastDigits: null,
        realName: splittedGuide[0],
        retryAt,
        // FIXME: (standardize-document-content): 사용되지 않습니다. 추후 타입 정리가 필요합니다.
        status: auth.status,
      };
    }

    if (auth.authType === 'PASSWORD') {
      transformedAuthentication.password = {
        authType: auth.authType,
        authenticationId: auth.authenticationId,
        failureCount: auth.failureCount,
        lastFailedAt: auth.lastFailedAt,
        passwordHint: auth.authGuide,
        retryAt,
        // FIXME: (standardize-document-content): 사용되지 않습니다. 추후 타입 정리가 필요합니다.
        status: auth.status,
      };
    }
  });

  return {
    authentication: transformedAuthentication,
    authKey,
  };
});

/**
 * 계약 인증 횟수 초과 상태를 초기화한다.
 */

export function contractAuthenticationExceedCountInitialize() {
  return {
    type: CONTRACT_AUTHENTICATION_EXCEED_COUNT_INITIALIZE,
  };
}

/**
 * 계약 인증 횟수 초과 상태를 초기화한다.
 */
export function contractAuthenticationFailureCountUpdate(
  authenticationId: string,
  failureCount: number,
  lastFailedAt?: string | null
) {
  return {
    type: CONTRACT_AUTHENTICATION_UPDATE_FAILURE_COUNT,
    payload: {
      authenticationId,
      failureCount,
      lastFailedAt,
    },
  };
}

export function initializeContractAuthentication() {
  return {
    type: CONTRACT_AUTHENTICATION_INITIALIZE,
  };
}

export type ContractAuthenticationAction =
  | ReturnType<typeof contractAuthenticationDigitalCertificateComplete>
  | ReturnType<typeof contractAuthenticationDigitalCertificateGuideCheck>
  | ReturnType<typeof contractAuthenticationDigitalCertificateGuideCheckFailure>
  | ReturnType<typeof contractAuthenticationDigitalCertificateGuideCheckSuccess>
  | ReturnType<typeof contractAuthenticationExceedCountInitialize>
  | ReturnType<typeof contractAuthenticationFailureCountUpdate>
  | ReturnType<typeof contractAuthenticationMessageRead>
  | ReturnType<typeof contractAuthenticationPasswordAuthenticateFailure>
  | ReturnType<typeof contractAuthenticationPasswordAuthenticateRequest>
  | ReturnType<typeof contractAuthenticationPasswordAuthenticateSuccess>
  | ReturnType<typeof contractAuthenticationPhoneCompleteFailure>
  | ReturnType<typeof contractAuthenticationPhoneCompleteSuccess>
  | ReturnType<typeof contractAuthenticationPhoneCertificateFailure>
  | ReturnType<typeof contractAuthenticationPhoneCertificateRequest>
  | ReturnType<typeof contractAuthenticationPhoneCertificateSuccess>
  | ReturnType<typeof contractAuthenticationPhoneRetryFailure>
  | ReturnType<typeof contractAuthenticationPhoneRetryRequest>
  | ReturnType<typeof contractAuthenticationPhoneRetrySuccess>
  | ReturnType<typeof contractAuthenticationPhoneDetailStatusInitialize>
  | ReturnType<typeof contractAuthenticationPhoneDetailStatusUpdate>
  | ReturnType<typeof fetchAuthenticationFailureStatus>
  | ReturnType<typeof fetchAuthenticationFailureStatusFailure>
  | ReturnType<typeof fetchAuthenticationFailureStatusSuccess>
  | ReturnType<typeof initializeContractAuthentication>;
